import React, { useState } from 'react';

import * as styles from './styles';
import VehicleCell from './VehicleCell/VehicleCell';
import VehicleMaintenanceItemsTable from './VehicleMaintenanceItemsTable/VehicleMaintenanceItemsTable';
import {
  Table as BaseTable,
  TableRow as BaseTableRow,
  Collapse,
  TableBody,
  Typography,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import moment from 'moment/moment';

import type { Vehicle } from '@cobliteam/common-types';

import type {
  PastMaintenance,
  PastMaintenanceWithVehicle,
} from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import {
  ChevronIcon,
  type ITableColumn,
  type ITableRow,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSorter,
} from '@cobliteam/base-components';

import { currencyFormatter, numberFormatter } from '../../../utils';

enum PastMaintenanceId {
  DATE = 'date',
  VEHICLE = 'vehicle',
  ODOMETER = 'odometer',
  CATEGORY = 'category',
  RECURRENCE = 'recurrence',
  TOTAL_COST = 'totalCost',
}

interface PastMaintenanceRow extends ITableRow {
  date: number;
  vehicle: Vehicle;
  odometer: number;
  category: string;
  recurrence: string;
  totalCost?: number;
}

interface PastMaintenanceColumn extends ITableColumn<PastMaintenanceRow> {
  id: PastMaintenanceId;
  label: string;
}

const pastMaintenanceTableColumns: PastMaintenanceColumn[] = [
  {
    id: PastMaintenanceId.DATE,
    label: 'Data da manutenção',
    className: styles.columnWithFixedWidth('50px'),
    sortable: true,
    orderByKey: (row: PastMaintenanceRow) => row.date,
    Cell: (row: PastMaintenanceRow) =>
      moment(row.date, 'x').format('DD/MM/YYYY'),
  },
  {
    id: PastMaintenanceId.VEHICLE,
    label: 'Veículo',
    sortable: true,
    className: styles.columnWithFixedWidth('170px'),
    orderByKey: (row: PastMaintenanceRow) => row.vehicle.label,
    Cell: (row: PastMaintenanceRow) => <VehicleCell vehicle={row!.vehicle} />,
  },
  {
    id: PastMaintenanceId.ODOMETER,
    label: 'Odômetro na data',
    Cell: (row: PastMaintenanceRow) =>
      `${numberFormatter.format(Math.round(row.odometer / 1000))} km`,
  },
  {
    id: PastMaintenanceId.CATEGORY,
    label: 'Categoria',
    sortable: true,
    Cell: (row: PastMaintenanceRow) => row.category,
  },
  {
    id: PastMaintenanceId.RECURRENCE,
    label: 'Recorrência',
    Cell: (row: PastMaintenanceRow) => row.recurrence,
  },
  {
    id: PastMaintenanceId.TOTAL_COST,
    label: '',
    Cell: (row: PastMaintenanceRow) =>
      row?.totalCost ? (
        <span className={styles.totalCostCell}>{`R$ ${currencyFormatter.format(
          row?.totalCost
        )}`}</span>
      ) : (
        ''
      ),
  },
];

interface VehicleMaintenanceTableProps {
  pastMaintenances: PastMaintenanceWithVehicle[];
  onEditClick: (maintenance: PastMaintenance) => void;
}

export const VehicleMaintenanceTable = ({
  pastMaintenances,
  onEditClick,
}: VehicleMaintenanceTableProps) => {
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<ITableColumn<PastMaintenanceRow>>(
    pastMaintenanceTableColumns[0]
  );

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  const [openRows, setOpenRows] = useState<string[]>([]);
  const totalPastMaintenancesRowsLength = pastMaintenances.length;

  const handleCurrentPageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleRequestSort = (newOrderBy: ITableColumn<PastMaintenanceRow>) => {
    const newOrder: 'asc' | 'desc' =
      orderBy === newOrderBy && order === 'desc' ? 'asc' : 'desc';
    Analytics.track('Maintenance History Table Sort Request', {
      column: newOrderBy.id,
    });

    setOrder(newOrder);
    setOrderBy(newOrderBy);
  };

  const handleRowClick = (id: string) => {
    setOpenRows((prevOpenRow) => {
      if (prevOpenRow.includes(id)) {
        Analytics.track('Maintenance History Entry Clicked', {
          status: 'close',
        });
        return prevOpenRow.filter((rowId) => rowId !== id);
      }

      Analytics.track('Maintenance History Entry Clicked', {
        status: 'open',
      });
      return [...prevOpenRow, id];
    });
  };

  const getPastMaintenanceRows = (): PastMaintenanceRow[] => {
    return pastMaintenances.map((pastMaintenance) => {
      const types = pastMaintenance.types
        .map((type) => {
          return type === 'corrective'
            ? 'Corretiva'
            : type === 'preventive'
              ? 'Preventiva'
              : '';
        })
        .join(', ');

      const periodicitiesArray = [
        ...(pastMaintenance.periodicitiesInMeters || []),
      ].sort((a, b) => a - b);
      const periodicities = periodicitiesArray
        .map(
          (value) => `${numberFormatter.format(Math.round(value / 1000))} km`
        )
        .join(', ');

      return {
        id: pastMaintenance.id,
        date: pastMaintenance.date,
        vehicle: pastMaintenance.vehicle,
        odometer: pastMaintenance.odometerInMeters,
        category: types,
        recurrence: periodicities,
        totalCost: pastMaintenance.totalCost,
      };
    });
  };

  const pastMaintenanceRows = getPastMaintenanceRows();
  const pastMaintenanceWantedRows = TableSorter.sort(
    pastMaintenanceRows,
    order,
    orderBy
  ).slice();
  const pastMaintenanceRenderedRows = pastMaintenanceWantedRows.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const getSelectedPastMaintenance = (
    pastMaintenanceId: string
  ): PastMaintenanceWithVehicle => {
    return pastMaintenances.find(
      (pastMaintenance) => pastMaintenance.id === pastMaintenanceId
    )!;
  };

  return (
    <div>
      <BaseTable className={styles.tableStyle} aria-labelledby='Manutenções'>
        <TableHead
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          columns={pastMaintenanceTableColumns}
          expandable
        />
        <TableBody>
          {pastMaintenanceRenderedRows.map((row) => {
            const pastMaintenanceIsOpen = openRows.includes(row.id);

            return (
              <React.Fragment key={row.id}>
                <TableRow
                  key={row.id}
                  id={row.id}
                  className={styles.tableRow}
                  onClick={() => handleRowClick(row.id)}
                >
                  {pastMaintenanceTableColumns.map((column) => {
                    return (
                      <TableCell
                        key={`${row.id}.${column.id}`}
                        className={
                          column.classNameFunc
                            ? column.classNameFunc(row)
                            : column.className
                        }
                      >
                        <Typography variant='inherit' noWrap>
                          {column.Cell(row)}
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align='center' size='small'>
                    <IconButton size='small'>
                      <ChevronIcon
                        className={styles.chevron(!pastMaintenanceIsOpen)}
                      />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <BaseTableRow>
                  <TableCell
                    style={{
                      paddingLeft: 0,
                      paddingRight: 0,
                    }}
                    colSpan={pastMaintenanceTableColumns.length + 1}
                  >
                    <Collapse
                      in={pastMaintenanceIsOpen}
                      timeout='auto'
                      unmountOnExit
                    >
                      <VehicleMaintenanceItemsTable
                        pastMaintenance={getSelectedPastMaintenance(row.id)}
                        onEditClick={onEditClick}
                      />
                    </Collapse>
                  </TableCell>
                </BaseTableRow>
                <TableRow className={styles.rowSeparator} />
              </React.Fragment>
            );
          })}
        </TableBody>
      </BaseTable>
      {!!totalPastMaintenancesRowsLength && (
        <TablePagination
          count={totalPastMaintenancesRowsLength}
          page={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={handleCurrentPageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}
    </div>
  );
};
